import React, { useState, useEffect } from 'react';
import { Sparkles, Check, AlertCircle, Loader2, Info, Crown } from 'lucide-react';
import { Link } from 'react-router-dom';
import { Card } from '../../components/ui/Card';
import { Loading } from '../../components/ui/Loading';
import { useAiSettings, useTestAiProvider, useUpdateAiSettings } from '../../hooks/useAiSettings';
import { useOrganization } from '../../hooks/useOrganization';
import { AiProvider } from '../../types';

const AiSettings: React.FC = () => {
    const { data: settings, isLoading } = useAiSettings();
    const { data: organization } = useOrganization();
    const testProviderMutation = useTestAiProvider();
    const updateSettingsMutation = useUpdateAiSettings();

    const [selectedProvider, setSelectedProvider] = useState<AiProvider>('rule_based');
    const [config, setConfig] = useState<Record<string, any>>({});
    const [testResult, setTestResult] = useState<{ valid: boolean; message: string } | null>(null);
    const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);

    useEffect(() => {
        if (settings) {
            setSelectedProvider(settings.current_provider);
            setConfig(settings.current_config || {});
        }
    }, [settings]);

    const handleProviderSelect = (provider: AiProvider) => {
        setSelectedProvider(provider);
        setConfig({});
        setTestResult(null);
        setHasUnsavedChanges(true);
    };

    const handleConfigChange = (field: string, value: any) => {
        setConfig((prev) => ({ ...prev, [field]: value }));
        setTestResult(null);
        setHasUnsavedChanges(true);
    };

    const handleTest = async () => {
        setTestResult(null); // Clear previous result
        try {
            const result = await testProviderMutation.mutateAsync({
                provider: selectedProvider,
                config,
            });
            setTestResult(result);
        } catch (error) {
            // Error is already handled by the mutation's onError
            // This catch is for unexpected errors only
            console.error('Unexpected test error:', error);
        }
    };

    const handleSave = async () => {
        try {
            await updateSettingsMutation.mutateAsync({
                provider: selectedProvider,
                config,
            });
            setHasUnsavedChanges(false);
            setTestResult(null); // Clear test result on successful save
        } catch (error) {
            // Error is already handled by the mutation's onError
            console.error('Failed to save settings:', error);
        }
    };

    if (isLoading) {
        return <Loading />;
    }

    if (!settings) {
        return (
            <div className="p-6">
                <div className="text-center text-gray-500">Failed to load AI settings</div>
            </div>
        );
    }

    // Check if AI features are available for current plan
    const currentPlan = organization?.plan?.code || (organization?.plan as any) || 'free';
    const isAiAvailable = currentPlan === 'standard' || currentPlan === 'pro';

    // Helper to check if a provider is locked
    const isProviderLocked = (provider: AiProvider) => {
        // Rule-based is always available
        if (provider === 'rule_based') return false;
        // OpenAI and Gemini require Standard or Pro plan
        return !isAiAvailable;
    };

    const currentProviderInfo = settings.available_providers.find((p) => p.id === selectedProvider);

    return (
        <div className="p-6 max-w-6xl mx-auto space-y-6">
            {/* Header */}
            <div className="flex items-center justify-between">
                <div>
                    <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                        <Sparkles className="w-7 h-7 text-purple-600" />
                        AI Explanation Settings
                    </h1>
                    <p className="text-gray-600 mt-1">
                        Configure AI providers to generate intelligent explanations for detected risks
                    </p>
                </div>
            </div>

            {/* Info Banner */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex gap-3">
                <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-blue-800">
                    <p className="font-medium mb-1">How AI Explanations Work</p>
                    <p>
                        AI explanations help you understand why risks were detected and what actions to take.
                        Choose between free rule-based explanations or advanced AI models from OpenAI GPT or Google
                        Gemini (including the latest Gemini 2.0 Flash).
                    </p>
                </div>
            </div>

            {/* Plan Upgrade Warning */}
            {!isAiAvailable && (
                <div className="bg-orange-50 border-l-4 border-orange-400 rounded-r-lg p-4 flex gap-3">
                    <Crown className="w-5 h-5 text-orange-600 flex-shrink-0 mt-0.5" />
                    <div className="flex-1">
                        <p className="text-sm font-semibold text-orange-800 mb-1">
                            AI Providers Locked
                        </p>
                        <p className="text-sm text-orange-700">
                            OpenAI and Gemini are only available on <strong>Standard</strong> and <strong>Pro</strong> plans.
                            You're currently on the <strong className="capitalize">{currentPlan}</strong> plan.
                        </p>
                        <Link
                            to="/pricing"
                            className="inline-flex items-center gap-1 mt-2 text-sm font-medium text-orange-900 hover:text-orange-950 underline"
                        >
                            <Crown className="w-4 h-4" />
                            Upgrade to unlock AI features →
                        </Link>
                    </div>
                </div>
            )}

            {/* Provider Selection */}
            <div className="grid gap-4">
                <h2 className="text-lg font-semibold text-gray-900">Choose AI Provider</h2>
                <div className="grid md:grid-cols-3 gap-4">
                    {settings.available_providers.map((provider) => {
                        const isLocked = isProviderLocked(provider.id);
                        const isSelected = selectedProvider === provider.id;

                        return (
                            <Card
                                key={provider.id}
                                className={`cursor-pointer transition-all ${isLocked
                                        ? 'opacity-60 cursor-not-allowed'
                                        : 'hover:shadow-md'
                                    } ${isSelected
                                        ? 'border-2 border-purple-500 bg-purple-50'
                                        : 'border border-gray-200'
                                    }`}
                                onClick={() => !isLocked && handleProviderSelect(provider.id)}
                            >
                                <div className="relative">
                                    {isLocked && (
                                        <div className="absolute top-0 right-0 bg-orange-500 text-white rounded-bl-lg rounded-tr-lg px-2 py-1 flex items-center gap-1">
                                            <Crown className="w-3 h-3" />
                                            <span className="text-xs font-medium">Locked</span>
                                        </div>
                                    )}
                                    {isSelected && !isLocked && (
                                        <div className="absolute top-0 right-0 bg-purple-600 text-white rounded-bl-lg rounded-tr-lg px-2 py-1">
                                            <Check className="w-4 h-4" />
                                        </div>
                                    )}
                                    <h3 className="text-lg font-semibold text-gray-900 mb-2">{provider.name}</h3>
                                    <p className="text-sm text-gray-600 mb-3">{provider.description}</p>
                                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                                        <span className="text-xs font-medium text-gray-500">Cost</span>
                                        <span className="text-sm font-semibold text-purple-600">{provider.cost}</span>
                                    </div>
                                    {isLocked && (
                                        <div className="mt-2 pt-2 border-t border-orange-200">
                                            <p className="text-xs text-orange-700 font-medium">
                                                Requires Standard or Pro plan
                                            </p>
                                        </div>
                                    )}
                                </div>
                            </Card>
                        );
                    })}
                </div>
            </div>

            {/* Provider Configuration */}
            {currentProviderInfo && currentProviderInfo.requires_config && (
                <Card>
                    <h2 className="text-lg font-semibold text-gray-900 mb-4">Configuration</h2>
                    <div className="space-y-4">
                        {Object.entries(currentProviderInfo.config_fields || {}).map(([fieldKey, field]) => (
                            <div key={fieldKey}>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    {field.label}
                                    {field.required && <span className="text-red-500 ml-1">*</span>}
                                </label>

                                {field.type === 'password' && (
                                    <input
                                        type="password"
                                        value={config[fieldKey] || ''}
                                        onChange={(e) => handleConfigChange(fieldKey, e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                        placeholder={`Enter ${field.label.toLowerCase()}`}
                                    />
                                )}

                                {field.type === 'select' && field.options && (
                                    <select
                                        value={config[fieldKey] || ''}
                                        onChange={(e) => handleConfigChange(fieldKey, e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                    >
                                        <option value="">Select {field.label.toLowerCase()}</option>
                                        {Object.entries(field.options).map(([value, label]) => (
                                            <option key={value} value={value}>
                                                {label}
                                            </option>
                                        ))}
                                    </select>
                                )}

                                {field.type === 'text' && (
                                    <input
                                        type="text"
                                        value={config[fieldKey] || ''}
                                        onChange={(e) => handleConfigChange(fieldKey, e.target.value)}
                                        className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-transparent outline-none"
                                        placeholder={`Enter ${field.label.toLowerCase()}`}
                                    />
                                )}

                                {field.help && (
                                    <p className="text-xs text-gray-500 mt-1">
                                        {field.help}
                                    </p>
                                )}
                            </div>
                        ))}

                        {/* Test Result */}
                        {testResult && (
                            <div
                                className={`p-4 rounded-lg flex items-start gap-3 ${testResult.valid
                                    ? 'bg-green-50 border border-green-200'
                                    : 'bg-red-50 border border-red-200'
                                    }`}
                            >
                                {testResult.valid ? (
                                    <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
                                ) : (
                                    <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
                                )}
                                <div>
                                    <p
                                        className={`text-sm font-medium ${testResult.valid ? 'text-green-800' : 'text-red-800'
                                            }`}
                                    >
                                        {testResult.message}
                                    </p>
                                </div>
                            </div>
                        )}

                        {/* Test Button */}
                        <button
                            onClick={handleTest}
                            disabled={
                                testProviderMutation.isPending ||
                                (currentProviderInfo?.requires_config &&
                                    Object.entries(currentProviderInfo.config_fields || {}).some(
                                        ([key, field]) => field.required && !config[key]
                                    )
                                )
                            }
                            className="w-full bg-gray-100 text-gray-700 py-2 px-4 rounded-lg font-medium hover:bg-gray-200 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center justify-center gap-2"
                        >
                            {testProviderMutation.isPending ? (
                                <>
                                    <Loader2 className="w-4 h-4 animate-spin" />
                                    Testing...
                                </>
                            ) : (
                                'Test Configuration'
                            )}
                        </button>
                        {currentProviderInfo?.requires_config &&
                            Object.entries(currentProviderInfo.config_fields || {}).some(
                                ([key, field]) => field.required && !config[key]
                            ) && (
                                <p className="text-xs text-gray-500 mt-2">
                                    Please fill in all required fields to test
                                </p>
                            )}
                    </div>
                </Card>
            )}

            {/* Save Button */}
            <div className="flex flex-col gap-3">
                <div className="flex justify-end gap-3">
                    {hasUnsavedChanges && (
                        <span className="text-sm text-orange-600 flex items-center gap-1 mr-auto">
                            <AlertCircle className="w-4 h-4" />
                            You have unsaved changes
                        </span>
                    )}
                    <button
                        onClick={handleSave}
                        disabled={
                            updateSettingsMutation.isPending ||
                            !hasUnsavedChanges ||
                            (currentProviderInfo?.requires_config &&
                                Object.entries(currentProviderInfo.config_fields || {}).some(
                                    ([key, field]) => field.required && !config[key]
                                )
                            )
                        }
                        className="bg-purple-600 text-white py-2 px-6 rounded-lg font-medium hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors flex items-center gap-2"
                    >
                        {updateSettingsMutation.isPending ? (
                            <>
                                <Loader2 className="w-4 h-4 animate-spin" />
                                Saving...
                            </>
                        ) : (
                            'Save Settings'
                        )}
                    </button>
                </div>

                {/* Required fields warning */}
                {currentProviderInfo?.requires_config &&
                    Object.entries(currentProviderInfo.config_fields || {}).some(
                        ([key, field]) => field.required && !config[key]
                    ) && (
                        <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-sm text-red-800">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-red-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium">Required fields missing</p>
                                    <p className="text-xs mt-1">
                                        Please fill in all required fields before saving.
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}

                {/* Warning if provider requires config but not tested or test failed */}
                {currentProviderInfo?.requires_config && hasUnsavedChanges && (
                    testResult === null || testResult.valid === false
                ) && (
                        <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-3 text-sm text-yellow-800">
                            <div className="flex items-start gap-2">
                                <AlertCircle className="w-4 h-4 text-yellow-600 flex-shrink-0 mt-0.5" />
                                <div>
                                    <p className="font-medium">
                                        {testResult === null
                                            ? '⚠️ Configuration not tested'
                                            : '❌ Test failed'}
                                    </p>
                                    <p className="text-xs mt-1">
                                        {testResult === null
                                            ? 'It\'s recommended to test your configuration before saving.'
                                            : 'Saving with invalid configuration may cause AI explanations to fail.'}
                                    </p>
                                </div>
                            </div>
                        </div>
                    )}
            </div>

            {/* Cost Information */}
            <Card className="bg-gray-50">
                <h3 className="text-sm font-semibold text-gray-900 mb-3">💰 Cost Estimates</h3>
                <div className="space-y-3 text-sm">
                    <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                        <div>
                            <span className="font-medium text-gray-900">Rule-Based AI</span>
                            <span className="text-xs text-gray-500 ml-2">(Default)</span>
                        </div>
                        <span className="font-semibold text-green-600">$0 Free</span>
                    </div>
                    <div className="flex justify-between items-center pb-2 border-b border-gray-200">
                        <div>
                            <span className="font-medium text-gray-900">OpenAI GPT-4o Mini</span>
                            <span className="text-xs text-gray-500 block">Faster & cheaper</span>
                        </div>
                        <span className="font-medium text-gray-700">~$0.0003/ea</span>
                    </div>
                    <div className="space-y-2">
                        <div className="font-medium text-gray-900 flex items-center gap-2">
                            <span>Google Gemini</span>
                            <span className="text-xs bg-purple-100 text-purple-700 px-2 py-0.5 rounded">Latest!</span>
                        </div>
                        <div className="pl-4 space-y-1.5 text-xs text-gray-600">
                            <div className="flex justify-between">
                                <span>• Gemini 2.0 Flash (Latest)</span>
                                <span className="font-medium text-green-600">Free</span>
                            </div>
                            <div className="flex justify-between">
                                <span>• Gemini 1.5 Flash</span>
                                <span className="font-medium">1500/day free</span>
                            </div>
                            <div className="flex justify-between">
                                <span>• Gemini 1.5 Pro</span>
                                <span className="font-medium">1500/day free</span>
                            </div>
                        </div>
                    </div>
                </div>
            </Card>
        </div>
    );
};

export default AiSettings;
